import React from "react";
import { RecipeComponent } from "../../types/recipe";
import { formatAmountWithGrams } from "../../utils/measurementUnits";

interface RecipeComponentsListProps {
  components: RecipeComponent[];
}

// Group components by section, preserving position order.
const groupBySection = (components: RecipeComponent[]) => {
  const sorted = [...components].sort((a, b) => a.position - b.position);
  const groups: { section: string; items: RecipeComponent[] }[] = [];
  for (const component of sorted) {
    const sectionName = component.section || "Main";
    let group = groups.find((g) => g.section === sectionName);
    if (!group) {
      group = { section: sectionName, items: [] };
      groups.push(group);
    }
    group.items.push(component);
  }
  return groups;
};

const RecipeComponentsList: React.FC<RecipeComponentsListProps> = ({
  components,
}) => {
  const groups = groupBySection(components);

  return (
    <div className="space-y-6">
      {groups.map((group, groupIndex) => (
        <div key={groupIndex}>
          <h4 className="mb-3 text-lg font-semibold uppercase text-navy-700 dark:text-white">
            {group.section}
          </h4>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {group.items.map((item) => (
              <div
                key={item.id}
                className="flex items-center justify-between rounded-lg bg-gray-50 p-3 dark:bg-gray-800"
              >
                <span className="min-w-0 flex-1 pr-2 font-medium text-navy-700 dark:text-white">
                  {item.ingredient?.name || "Unknown ingredient"}
                </span>
                <span className="min-w-[56px] shrink-0 whitespace-nowrap text-right font-medium text-gray-600 dark:text-gray-400">
                  {formatAmountWithGrams(
                    item.portion_grams,
                    item.quantity,
                    item.measurement_unit_label
                  )}
                </span>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

export default RecipeComponentsList;
